import {updateForm, useAutoForm, useSelector} from '../../../store.js'
import {usePresetsSelector} from '../../../data/company.js'
import SettingsPanel from '../SettingsPanel.js'
import PresetsFeaturePage from './PresetsFeaturePage.js'
import {
  presetOptionsSelector,
  presetOptionTokensSelector,
} from '../../../data/presets.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'
import {showEditPresetModal} from '../../LabelConfig/Modals/EditPresetModal/index.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'
import {showDeletePresetModal} from '../../LabelConfig/Modals/DeletePresetModal.js'
import {filterPresets} from '../../LabelConfig/SingleLabelConfigForm/PresetsDropdown.js'
import {useCallback, useEffect} from 'react'
import TextInput from '../../../common/components/TextInput.js'
import Icon from '../../../common/icons/Icon.js'
import {settingsPermissionsSelector} from '../settingsFunctions.js'
import {PRESETS} from '../../../common/constants/SettingsPanels.js'
import {showPresetSortModal} from './PresetSortModal.js'

function PresetRow({preset}) {
  return (
    <li className="list__item flex--justify divider--bottom">
      <div className="w-80">
        <div>
          <strong className="fs-01 lh-md">{preset.name}</strong>
        </div>
        <div>
          <ButtonLink
            className="fs-n0 lh-md"
            onClick={() => showEditPresetModal({presetID: preset.id})}
          >
            Edit
          </ButtonLink>
        </div>
      </div>
      <div>
        <ButtonLink
          className="no-underline"
          title="Delete preset"
          onClick={() => showDeletePresetModal(preset.id)}
        >
          <Icon name="trash" className="fs-00 lh-md" />
        </ButtonLink>
      </div>
    </li>
  )
}

export default function Presets() {
  const usePresets = useSelector(usePresetsSelector)
  const formName = 'presets_settings_list'

  const form = useAutoForm(formName, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    serverError: null,
  })

  const presetOptions = useSelector(presetOptionsSelector)
  const presetOptionTokens = useSelector(presetOptionTokensSelector)
  const hasPresets = presetOptions.length > 0
  const hasPermission = useSelector(settingsPermissionsSelector)[PRESETS]

  const onChange = useCallback(() =>
    filterPresets(formName, presetOptions, presetOptionTokens),
  )

  useEffect(() => {
    onChange()
  }, [presetOptions, form.text])

  if (!usePresets) {
    return <PresetsFeaturePage />
  }

  return (
    <SettingsPanel header="Shipping Presets" hasPermission={hasPermission}>
      {!hasPresets ? (
        <div className="row">
          <div className="medium-5 columns">
            <dl className="list">
              <dt className="list__title fs-02 lh-sm margin-bottom-15">
                Preset it and forget it.
              </dt>
              <dd className="fs-01 margin-bottom-30">
                Spending a ton of time filling out shipping information to
                create labels? Increase speed and efficiency with{' '}
                <a
                  className="btn--link darkest border-underline v-align-base"
                  href="https://support.ordoro.com/how-to-create-shipping-label-presets/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>Shipping Presets</strong>
                </a>
                , a feature that allows you to save and automatically apply
                shipping parameters to individual or bulk orders.
              </dd>
              <dd className="margin-bottom-0">
                <ButtonPrimary onClick={() => showEditPresetModal()} size="lg">
                  Create a Preset
                </ButtonPrimary>
              </dd>
            </dl>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="medium-7 columns">
            <div className="flex--justify align-items-center margin-bottom-15">
              <div className="flex align-items-center w-50">
                <TextInput
                  className="input--med-h margin-right-7"
                  placeholder="Search for an existing preset..."
                  value={form.text}
                  onChange={(text) => updateForm(formName, {text})}
                />
                {form.text && (
                  <ButtonLink
                    className="no-underline"
                    title="Clear search"
                    onClick={() => updateForm(formName, {text: ''})}
                  >
                    <Icon name="times" className="fs-00 lh-md" />
                  </ButtonLink>
                )}
              </div>
              <div className="flex">
                <ButtonPrimary onClick={() => showEditPresetModal()} size="md">
                  Create a Preset
                </ButtonPrimary>
                {form.results.length > 1 && (
                  <ButtonPrimary
                    size="sm"
                    className="flex align-items-center margin-left-10"
                    isOutlined
                    title="Set the order of presets"
                    onClick={() => showPresetSortModal()}
                  >
                    <Icon name="reorder" className="fs-02 lh-0" />
                  </ButtonPrimary>
                )}
              </div>
            </div>
            <hr className="margin-top-25 margin-bottom-15" />
            <ul className="list margin-bottom-25">
              {form.results.map((option) => (
                <PresetRow key={option.value} preset={option.entity} />
              ))}
              {!hasPresets && (
                <li className="list__item fs-01 lh-md">
                  <strong>No presets have been created.</strong>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </SettingsPanel>
  )
}
